<template>
  <div
    ref="menuContainer"
    :class="[
      $style.profileMenuContainer,
      props.placement === 'sidebar' && $style.sidebarPlacement,
    ]"
    @keydown.esc="closeMenu"
    @focusout="handleFocusOut"
  >
    <button
      ref="menuButton"
      type="button"
      :class="$style.profileMenuButton"
      aria-label="Open account menu"
      aria-haspopup="menu"
      :aria-expanded="isMenuOpen"
      @click="toggleMenu"
      @keydown.down.prevent="openMenu()"
      @keydown.up.prevent="openMenu(true)"
    >
      <UserIcon />
    </button>
    <ul
      v-if="isMenuOpen"
      :class="$style.profileMenu"
      role="menu"
      aria-label="Account"
      @keydown="handleMenuKeydown"
    >
      <li role="none">
        <button
          :class="[$style.menuItem, 'type-label-large']"
          role="menuitem"
          tabindex="-1"
          @click="goTo('/organizationSettings?tab=profile')"
        >
          My Profile
        </button>
      </li>
      <li role="none">
        <button
          :class="[$style.menuItem, 'type-label-large']"
          role="menuitem"
          tabindex="-1"
          @click="goTo('/organizationSettings')"
        >
          Settings
        </button>
      </li>
      <li role="none">
        <button
          :class="[$style.menuItem, 'type-label-large']"
          role="menuitem"
          tabindex="-1"
          @click="goTo('/help')"
        >
          Help &amp; Resources
        </button>
      </li>
      <li role="none">
        <button
          :class="[$style.menuItem, $style.logoutItem, 'type-label-large']"
          role="menuitem"
          tabindex="-1"
          :aria-disabled="loading"
          :aria-busy="loading"
          @click="handleSignout"
        >
          {{ loading ? 'Logging out…' : 'Log out' }}
        </button>
      </li>
    </ul>
  </div>
</template>

<script setup lang="ts">
  import UserIcon from '~/assets/svg/user.svg'

  interface Props {
    placement?: 'header' | 'sidebar'
  }

  const props = withDefaults(defineProps<Props>(), {
    placement: 'header',
  })

  const store = useSignupLoginStore()
  const isMenuOpen = ref(false)
  const loading = ref(false)
  const menuContainer = ref<HTMLElement | null>(null)
  const menuButton = ref<HTMLButtonElement | null>(null)
  const menuItems = () =>
    Array.from(
      menuContainer.value?.querySelectorAll<HTMLButtonElement>(
        '[role="menuitem"]',
      ) ?? [],
    )
  const openMenu = async (last = false) => {
    isMenuOpen.value = true
    await nextTick()
    if (!isMenuOpen.value) return
    const items = menuItems()
    items[last ? items.length - 1 : 0]?.focus()
  }
  const toggleMenu = () => {
    if (isMenuOpen.value) closeMenu()
    else void openMenu()
  }
  const closeMenu = () => {
    if (!isMenuOpen.value) return
    isMenuOpen.value = false
    menuButton.value?.focus()
  }

  const handleFocusOut = (event: FocusEvent) => {
    if (!menuContainer.value?.contains(event.relatedTarget as Node | null)) {
      isMenuOpen.value = false
    }
  }

  const handleMenuKeydown = (event: KeyboardEvent) => {
    // Restore the trigger before native Tab navigation so it can continue to
    // the next (or previous) control outside this composite menu.
    if (event.key === 'Tab') {
      closeMenu()
      return
    }
    const items = menuItems()
    const current = items.indexOf(document.activeElement as HTMLButtonElement)
    let next: number
    switch (event.key) {
      case 'ArrowDown':
        next = (current + 1) % items.length
        break
      case 'ArrowUp':
        next = (current - 1 + items.length) % items.length
        break
      case 'Home':
        next = 0
        break
      case 'End':
        next = items.length - 1
        break
      default: {
        if (
          event.key.length !== 1 ||
          event.ctrlKey ||
          event.metaKey ||
          event.altKey
        )
          return
        const ordered = [
          ...items.slice(current + 1),
          ...items.slice(0, current + 1),
        ]
        const match = ordered.find((item) =>
          item.textContent
            ?.trim()
            .toLowerCase()
            .startsWith(event.key.toLowerCase()),
        )
        if (!match) return
        event.preventDefault()
        match.focus()
        return
      }
    }
    event.preventDefault()
    items[next]?.focus()
  }

  const handlePointerDown = (event: PointerEvent) => {
    if (
      isMenuOpen.value &&
      event.target instanceof Node &&
      !menuContainer.value?.contains(event.target)
    ) {
      isMenuOpen.value = false
    }
  }

  const goTo = (path: string) => {
    isMenuOpen.value = false
    navigateTo(path)
  }

  const handleSignout = async () => {
    if (loading.value) return
    loading.value = true
    try {
      await store.logout()
    } catch (err) {
      console.log('>>>logout err', err)
    } finally {
      isMenuOpen.value = false
      loading.value = false
    }
  }

  onMounted(() => document.addEventListener('pointerdown', handlePointerDown))
  onUnmounted(() =>
    document.removeEventListener('pointerdown', handlePointerDown),
  )
</script>
<style module>
  .profileMenuContainer {
    position: relative;
  }
  .profileMenuButton {
    color: var(--color-icon-on-brand);
    width: 40px;
    height: 40px;
    border-radius: var(--radius-md);
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    padding: 0;
    border: 0;
    cursor: pointer;
    background: var(--gradient-brand-bold);
  }

  .profileMenuButton svg {
    width: var(--space-20);
    height: var(--space-24);
  }

  .profileMenuButton:focus-visible,
  .menuItem:focus-visible {
    outline: 2px solid var(--color-interactive-primary);
    outline-offset: 2px;
  }

  .profileMenuButton:hover {
    border: 3px solid var(--color-interactive-tertiary-selected-border);
    background: var(--gradient-brand-bold);
  }

  .profileMenuButton:active {
    transform: scale(0.97);
  }

  .sidebarPlacement .profileMenuButton {
    width: var(--space-48);
    height: var(--space-48);
    color: var(--color-interactive-primary);
    background: var(--color-surface-accent);
    border-radius: var(--radius-md);
  }

  .sidebarPlacement .profileMenuButton:hover {
    border: 1px solid var(--color-interactive-tertiary-selected-border);
    background: var(--color-interactive-tertiary-selected);
  }

  .profileImage {
    height: 100%;
    width: 100%;
    object-fit: cover;
  }

  .profileMenu {
    position: absolute;
    top: calc(100% - var(--space-4));
    right: 0;
    left: auto;
    margin: 0;
    padding: var(--space-8);
    background-color: var(--color-surface-default);
    box-shadow: var(--elevation-md);
    border-radius: var(--radius-md);
    border: 1px solid var(--color-border-default);
    width: 190px;
    list-style: none;
    z-index: 110;
  }

  .sidebarPlacement .profileMenu {
    top: auto;
    right: auto;
    bottom: calc(100% + var(--space-8));
    left: 0;
    transform-origin: bottom left;
  }

  .menuItem {
    width: 100%;
    min-height: 38px;
    height: auto;
    justify-content: flex-start;
    padding: var(--space-8) var(--space-12);
    border: 0;
    border-radius: var(--radius-sm);
    background: transparent;
    color: var(--color-text-primary);
    text-align: left;
  }

  .menuItem:hover {
    background-color: var(--color-interactive-tertiary-hover);
    color: var(--color-interactive-primary);
    border: 0;
  }

  .logoutItem {
    color: var(--color-status-error);
  }

  .logoutItem:hover {
    color: var(--color-status-error);
    background: var(--color-status-error-subtle);
  }
</style>
